
import React, { useState, useEffect } from 'react';
import {
  Palette, Layout, Smartphone, Monitor, Save, Plus, Trash2,
  ChevronRight, Sparkles, Loader2, Wand2, ArrowUp, ArrowDown,
  Wrench, Type, ImageIcon, Edit3, X, Bot, Send, CheckCircle2,
  Upload, Layers, Globe, FileText, MousePointer2, LogOut, ChevronLeft, Settings
} from 'lucide-react';
import { WebsiteConfig, WebsiteSection, UserRole, User } from '../types';
import { supabase_get_site_config, supabase_save_site_config, supabase_upload_website_asset } from '../services/supabaseService';
import { generateWebsiteSection, refineWebsiteText } from '../services/geminiService';
import LandingPage from './LandingPage';

interface WebsiteEditorProps {
  user: User;
  onClose: () => void;
  vacancies?: any[];
}

const WebsiteEditor: React.FC<WebsiteEditorProps> = ({ user, onClose, vacancies: initialVacancies = [] }) => {
  const [config, setConfig] = useState<WebsiteConfig>({
    primaryColor: '#e51b23',
    fontFamily: 'Inter, sans-serif',
    pages: {
      home: []
    }
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState<string | null>(null);
  const [activePage, setActivePage] = useState<string>('home');
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [isAiProcessing, setIsAiProcessing] = useState<string | null>(null);
  const [vacancies, setVacancies] = useState<any[]>(initialVacancies);

  useEffect(() => {
    const load = async () => {
      const data = await supabase_get_site_config();
      if (data) {
        if (!data.pages) {
          setConfig({ ...config, pages: { home: (data as any).sections || [] } });
        } else {
          setConfig(data);
        }
      }
      setIsLoading(false);
    };
    load();
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await supabase_save_site_config(config);
      alert("Website gepubliceerd!");
    } catch (e) {
      alert("Fout bij opslaan.");
    } finally {
      setIsSaving(false);
    }
  };

  const addSection = async (type: string) => {
    setIsAiProcessing('add');
    try {
      const prompt = `Maak een wervende sectie van type "${type}" voor de pagina "${activePage}"`;
      const newSectionData = await generateWebsiteSection(prompt, type);
      const newSection: WebsiteSection = {
        id: Math.random().toString(36).substr(2, 9),
        type: type as any,
        title: newSectionData.title,
        subtitle: newSectionData.subtitle,
        content: newSectionData.content,
        styles: newSectionData.styles,
        isVisible: true
      };

      const updatedPages = { ...config.pages };
      updatedPages[activePage] = [...(updatedPages[activePage] || []), newSection];
      setConfig({ ...config, pages: updatedPages });
      setSelectedSectionId(newSection.id);
    } catch (e) {
      console.error(e);
    } finally {
      setIsAiProcessing(null);
    }
  };

  const removeSection = (id: string) => {
    const updatedPages = { ...config.pages };
    updatedPages[activePage] = updatedPages[activePage].filter(s => s.id !== id);
    setConfig({ ...config, pages: updatedPages });
    if (selectedSectionId === id) setSelectedSectionId(null);
  };

  const moveSection = (index: number, direction: 'up' | 'down') => {
    const sections = [...config.pages[activePage]];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= sections.length) return;

    [sections[index], sections[newIndex]] = [sections[newIndex], sections[index]];
    const updatedPages = { ...config.pages };
    updatedPages[activePage] = sections;
    setConfig({ ...config, pages: updatedPages });
  };

  const updateSection = (id: string, updates: Partial<WebsiteSection>) => {
    const updatedPages = { ...config.pages };
    updatedPages[activePage] = updatedPages[activePage].map(s => s.id === id ? { ...s, ...updates } : s);
    setConfig({ ...config, pages: updatedPages });
  };

  const handleSectionImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, sectionId: string) => {
    if (!e.target.files?.[0]) return;
    setIsUploading(sectionId);
    try {
      const url = await supabase_upload_website_asset(e.target.files[0]);
      if (url) {
        const section = config.pages[activePage].find(s => s.id === sectionId);
        updateSection(sectionId, { content: { ...section?.content, imageUrl: url } });
      }
    } catch (err) {
      alert("Upload mislukt.");
    } finally {
      setIsUploading(null);
    }
  };

  const handleAiRefineText = async (sectionId: string, field: 'title' | 'subtitle') => {
    const section = config.pages[activePage].find(s => s.id === sectionId);
    if (!section) return;

    setIsAiProcessing(sectionId + field);
    try {
      const currentText = (section as any)[field];
      const refined = await refineWebsiteText(currentText, `Maak krachtiger voor de "${activePage}" pagina van een recruitment tool`);
      updateSection(sectionId, { [field]: refined });
    } catch (e) {
      console.error(e);
    } finally {
      setIsAiProcessing(null);
    }
  };

  if (isLoading) return null;

  const currentSections = config.pages[activePage] || [];
  const selectedSection = currentSections.find(s => s.id === selectedSectionId);

  return (
    <div className="fixed inset-0 z-[200] pointer-events-none overflow-hidden flex flex-col">
      {/* CMS SIDEBAR */}
      <div className="absolute top-0 right-0 w-[420px] h-full bg-white shadow-2xl border-l border-slate-200 pointer-events-auto flex flex-col animate-in slide-in-from-right duration-300">

        {/* Header */}
        <div className="p-6 border-b border-slate-100 bg-slate-900 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center text-white shadow-lg">
              <Settings size={20} />
            </div>
            <div>
              <h2 className="text-sm font-black uppercase tracking-tight">Website Beheer</h2>
              <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest">Live Editor Modus</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Action Bar */}
        <div className="p-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 bg-red-600 text-white rounded-md text-[10px] font-black uppercase tracking-widest hover:bg-red-700 transition-all flex items-center gap-2 shadow-lg disabled:opacity-50"
            >
              {isSaving ? <Loader2 className="animate-spin" size={12} /> : <Globe size={12} />} Publiceren
            </button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[9px] font-bold text-slate-400 uppercase">Page:</span>
            <select
              value={activePage}
              onChange={(e) => { setActivePage(e.target.value); setSelectedSectionId(null); }}
              className="p-1.5 bg-white border border-slate-200 rounded text-[10px] font-bold uppercase outline-none focus:border-red-500"
            >
              {['home', 'agencies', 'companies', 'employees', 'about', 'pricing', 'contact'].map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-8 custom-scrollbar pb-32">

          {/* SEO Settings */}
          <div className="space-y-4">
            <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center justify-between">
              SEO Instellingen
            </h3>
            <div className="space-y-3 p-4 bg-slate-50 border border-slate-200 rounded-lg">
              <div>
                <label className="text-[9px] font-black text-slate-400 uppercase mb-1.5 block">Meta Title (Pagina Titel)</label>
                <input
                  type="text"
                  value={config.seo?.title || ''}
                  onChange={(e) => setConfig({ ...config, seo: { ...config.seo, title: e.target.value } })}
                  placeholder="Bijv. Werken bij [Bedrijfsnaam]"
                  className="w-full p-2 bg-white border border-slate-200 rounded text-xs font-bold outline-none focus:border-red-500"
                />
              </div>
              <div>
                <label className="text-[9px] font-black text-slate-400 uppercase mb-1.5 block">Meta Description</label>
                <textarea
                  value={config.seo?.description || ''}
                  onChange={(e) => setConfig({ ...config, seo: { ...config.seo, description: e.target.value } })}
                  placeholder="Korte omschrijving voor Google..."
                  className="w-full p-2 bg-white border border-slate-200 rounded text-xs outline-none focus:border-red-500 h-16 resize-none"
                />
              </div>
            </div>
          </div>

          {/* Section Manager */}
          <div className="space-y-4">
            <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center justify-between">
              Structure <span>{currentSections.length}</span>
            </h3>

            <div className="space-y-2">
              {currentSections.map((section, idx) => (
                <div
                  key={section.id}
                  onClick={() => setSelectedSectionId(section.id)}
                  className={`p-4 border rounded-lg transition-all cursor-pointer group ${selectedSectionId === section.id ? 'border-red-600 bg-red-50/20 shadow-md ring-1 ring-red-100' : 'border-slate-100 hover:bg-slate-50'}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-black text-slate-300">0{idx + 1}</span>
                      <h4 className="text-xs font-black text-slate-800 uppercase tracking-tight truncate max-w-[180px]">{section.title || 'Naamloze sectie'}</h4>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={(e) => { e.stopPropagation(); moveSection(idx, 'up'); }} className="p-1 text-slate-400 hover:text-red-500 disabled:opacity-20"><ArrowUp size={14} /></button>
                      <button onClick={(e) => { e.stopPropagation(); moveSection(idx, 'down'); }} className="p-1 text-slate-400 hover:text-red-500 disabled:opacity-20"><ArrowDown size={14} /></button>
                      <button onClick={(e) => { e.stopPropagation(); removeSection(section.id); }} className="p-1 text-slate-400 hover:text-red-500"><Trash2 size={14} /></button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 bg-slate-100 rounded text-[8px] font-black text-slate-500 uppercase tracking-widest border border-slate-200">{section.type}</span>
                    {!section.isVisible && <span className="text-[8px] font-bold text-amber-600 uppercase">Hidden</span>}
                  </div>
                </div>
              ))}
            </div>

            <div className="pt-4 space-y-4">
              <h4 className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Sectie Toevoegen</h4>
              <div className="grid grid-cols-3 gap-2">
                {['hero', 'features', 'logos', 'testimonials', 'faq', 'cta', 'gallery', 'stats', 'video', 'text_only', 'team', 'pricing', 'contact', 'footer'].map(type => (
                  <button
                    key={type}
                    onClick={() => addSection(type)}
                    className="flex flex-col items-center justify-center gap-2 p-3 bg-slate-50 border border-slate-200 rounded-lg hover:border-red-500 hover:text-red-600 transition-all text-slate-400 group"
                  >
                    {isAiProcessing === 'add' ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />}
                    <span className="text-[8px] font-black uppercase tracking-tight">{type}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Field Editor */}
          {selectedSectionId && selectedSection && (
            <div className="space-y-6 pt-6 border-t border-slate-200 animate-in slide-in-from-bottom-2">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-black text-slate-900 uppercase flex items-center gap-2">
                  <Edit3 size={14} className="text-red-600" /> Bewerken: {selectedSection.type}
                </h3>
                <button onClick={() => setSelectedSectionId(null)} className="text-slate-400 hover:text-red-600"><X size={16} /></button>
              </div>

              <div className="space-y-4">
                {/* Visibility Toggle */}
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-md border border-slate-200">
                  <span className="text-[10px] font-black text-slate-500 uppercase">Zichtbaarheid</span>
                  <button
                    onClick={() => updateSection(selectedSectionId, { isVisible: !selectedSection.isVisible })}
                    className={`w-10 h-5 rounded-full transition-all relative ${selectedSection.isVisible ? 'bg-red-600' : 'bg-slate-300'}`}
                  >
                    <div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${selectedSection.isVisible ? 'left-6' : 'left-1'}`} />
                  </button>
                </div>

                {/* Title Field */}
                <div>
                  <label className="text-[9px] font-black text-slate-400 uppercase mb-1.5 block">Titel</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={selectedSection.title || ''}
                      onChange={(e) => updateSection(selectedSectionId, { title: e.target.value })}
                      className="w-full p-3 bg-slate-50 border border-slate-200 rounded-md text-xs font-bold outline-none focus:border-red-500"
                    />
                    <button
                      onClick={() => handleAiRefineText(selectedSectionId, 'title')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-300 hover:text-red-600"
                    >
                      {isAiProcessing === selectedSectionId + 'title' ? <Loader2 className="animate-spin" size={14} /> : <Sparkles size={14} />}
                    </button>
                  </div>
                </div>

                {/* Subtitle Field */}
                <div>
                  <label className="text-[9px] font-black text-slate-400 uppercase mb-1.5 block">Subtitel / Intro</label>
                  <div className="relative">
                    <textarea
                      value={selectedSection.subtitle || ''}
                      onChange={(e) => updateSection(selectedSectionId, { subtitle: e.target.value })}
                      className="w-full p-3 bg-slate-50 border border-slate-200 rounded-md text-xs font-medium h-24 resize-none outline-none focus:border-red-500"
                    />
                    <button
                      onClick={() => handleAiRefineText(selectedSectionId, 'subtitle')}
                      className="absolute right-2 bottom-2 text-slate-300 hover:text-red-600"
                    >
                      {isAiProcessing === selectedSectionId + 'subtitle' ? <Loader2 className="animate-spin" size={14} /> : <Sparkles size={14} />}
                    </button>
                  </div>
                </div>

                {/* Video URL Field */}
                {selectedSection.type === 'video' && (
                  <div>
                    <label className="text-[9px] font-black text-slate-400 uppercase mb-1.5 block">Video URL (YouTube/Vimeo)</label>
                    <input
                      type="url"
                      value={selectedSection.content?.videoUrl || ''}
                      onChange={(e) => updateSection(selectedSectionId, { content: { ...selectedSection.content, videoUrl: e.target.value } })}
                      className="w-full p-3 bg-slate-50 border border-slate-200 rounded-md text-xs font-bold outline-none focus:border-red-500"
                      placeholder="https://www.youtube.com/watch?v=..."
                    />
                  </div>
                )}

                {/* Items Editor (FAQ, Testimonials, Stats, Gallery) */}
                {['faq', 'testimonials', 'stats', 'gallery'].includes(selectedSection.type) && (
                  <div className="space-y-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center justify-between">
                      <label className="text-[9px] font-black text-slate-400 uppercase block">Items Beheren</label>
                      <button 
                        onClick={() => {
                          const newItems = [...(selectedSection.content?.items || []), {}];
                          updateSection(selectedSectionId, { content: { ...selectedSection.content, items: newItems } });
                        }}
                        className="text-[9px] text-red-600 font-bold uppercase hover:bg-red-50 px-2 py-1 rounded transition-colors"
                      >
                        + Item Toevoegen
                      </button>
                    </div>
                    {selectedSection.content?.items?.map((item: any, idx: number) => (
                      <div key={idx} className="bg-white p-3 border border-slate-200 rounded relative group">
                        <button 
                          onClick={() => {
                            const newItems = selectedSection.content!.items!.filter((_: any, i: number) => i !== idx);
                            updateSection(selectedSectionId, { content: { ...selectedSection.content, items: newItems } });
                          }}
                          className="absolute top-2 right-2 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X size={14} />
                        </button>
                        
                        {selectedSection.type === 'faq' && (
                          <div className="space-y-2 pr-6">
                            <input type="text" placeholder="Vraag" value={item.question || ''} onChange={e => {
                               const newItems = [...selectedSection.content!.items!]; newItems[idx] = { ...item, question: e.target.value };
                               updateSection(selectedSectionId, { content: { ...selectedSection.content, items: newItems } });
                            }} className="w-full p-2 border border-slate-100 rounded text-xs font-bold outline-none" />
                            <textarea placeholder="Antwoord" value={item.answer || ''} onChange={e => {
                               const newItems = [...selectedSection.content!.items!]; newItems[idx] = { ...item, answer: e.target.value };
                               updateSection(selectedSectionId, { content: { ...selectedSection.content, items: newItems } });
                            }} className="w-full p-2 border border-slate-100 rounded text-xs outline-none h-16 resize-none" />
                          </div>
                        )}

                        {selectedSection.type === 'testimonials' && (
                          <div className="space-y-2 pr-6">
                            <textarea placeholder="Quote" value={item.quote || ''} onChange={e => {
                               const newItems = [...selectedSection.content!.items!]; newItems[idx] = { ...item, quote: e.target.value };
                               updateSection(selectedSectionId, { content: { ...selectedSection.content, items: newItems } });
                            }} className="w-full p-2 border border-slate-100 rounded text-xs font-bold outline-none h-16 resize-none" />
                            <div className="grid grid-cols-2 gap-2">
                              <input type="text" placeholder="Naam" value={item.name || ''} onChange={e => {
                                 const newItems = [...selectedSection.content!.items!]; newItems[idx] = { ...item, name: e.target.value };
                                 updateSection(selectedSectionId, { content: { ...selectedSection.content, items: newItems } });
                              }} className="w-full p-2 border border-slate-100 rounded text-xs outline-none" />
                              <input type="text" placeholder="Rol" value={item.role || ''} onChange={e => {
                                 const newItems = [...selectedSection.content!.items!]; newItems[idx] = { ...item, role: e.target.value };
                                 updateSection(selectedSectionId, { content: { ...selectedSection.content, items: newItems } });
                              }} className="w-full p-2 border border-slate-100 rounded text-xs outline-none" />
                            </div>
                            <input type="url" placeholder="Avatar URL" value={item.avatar || ''} onChange={e => {
                               const newItems = [...selectedSection.content!.items!]; newItems[idx] = { ...item, avatar: e.target.value };
                               updateSection(selectedSectionId, { content: { ...selectedSection.content, items: newItems } });
                            }} className="w-full p-2 border border-slate-100 rounded text-xs outline-none" />
                          </div>
                        )}

                        {selectedSection.type === 'stats' && (
                          <div className="space-y-2 pr-6">
                            <input type="text" placeholder="Waarde (bijv. 100+)" value={item.value || ''} onChange={e => {
                               const newItems = [...selectedSection.content!.items!]; newItems[idx] = { ...item, value: e.target.value };
                               updateSection(selectedSectionId, { content: { ...selectedSection.content, items: newItems } });
                            }} className="w-full p-2 border border-slate-100 rounded text-xs font-bold outline-none text-red-600" />
                            <input type="text" placeholder="Label (bijv. Medewerkers)" value={item.label || ''} onChange={e => {
                               const newItems = [...selectedSection.content!.items!]; newItems[idx] = { ...item, label: e.target.value };
                               updateSection(selectedSectionId, { content: { ...selectedSection.content, items: newItems } });
                            }} className="w-full p-2 border border-slate-100 rounded text-xs outline-none" />
                          </div>
                        )}

                        {selectedSection.type === 'gallery' && (
                          <div className="space-y-2 pr-6">
                            <input type="url" placeholder="Afbeelding URL" value={item.imageUrl || ''} onChange={e => {
                               const newItems = [...selectedSection.content!.items!]; newItems[idx] = { ...item, imageUrl: e.target.value };
                               updateSection(selectedSectionId, { content: { ...selectedSection.content, items: newItems } });
                            }} className="w-full p-2 border border-slate-100 rounded text-xs outline-none" />
                            {item.imageUrl && (
                              <img src={item.imageUrl} className="h-20 w-full object-cover rounded mt-2 border border-slate-100" />
                            )}
                          </div>
                        )}

                        {selectedSection.type === 'team' && (
                          <div className="space-y-2 pr-6">
                            <input type="text" placeholder="Naam" value={item.name || ''} onChange={e => {
                               const newItems = [...selectedSection.content!.items!]; newItems[idx] = { ...item, name: e.target.value };
                               updateSection(selectedSectionId, { content: { ...selectedSection.content, items: newItems } });
                            }} className="w-full p-2 border border-slate-100 rounded text-xs font-bold outline-none" />
                            <input type="text" placeholder="Rol" value={item.role || ''} onChange={e => {
                               const newItems = [...selectedSection.content!.items!]; newItems[idx] = { ...item, role: e.target.value };
                               updateSection(selectedSectionId, { content: { ...selectedSection.content, items: newItems } });
                            }} className="w-full p-2 border border-slate-100 rounded text-xs outline-none" />
                            <textarea placeholder="Korte bio" value={item.description || ''} onChange={e => {
                               const newItems = [...selectedSection.content!.items!]; newItems[idx] = { ...item, description: e.target.value };
                               updateSection(selectedSectionId, { content: { ...selectedSection.content, items: newItems } });
                            }} className="w-full p-2 border border-slate-100 rounded text-xs outline-none h-16 resize-none" />
                            <input type="url" placeholder="Avatar URL" value={item.imageUrl || ''} onChange={e => {
                               const newItems = [...selectedSection.content!.items!]; newItems[idx] = { ...item, imageUrl: e.target.value };
                               updateSection(selectedSectionId, { content: { ...selectedSection.content, items: newItems } });
                            }} className="w-full p-2 border border-slate-100 rounded text-xs outline-none" />
                          </div>
                        )}

                        {selectedSection.type === 'pricing' && (
                          <div className="space-y-2 pr-6">
                            <input type="text" placeholder="Pakket Naam" value={item.name || ''} onChange={e => {
                               const newItems = [...selectedSection.content!.items!]; newItems[idx] = { ...item, name: e.target.value };
                               updateSection(selectedSectionId, { content: { ...selectedSection.content, items: newItems } });
                            }} className="w-full p-2 border border-slate-100 rounded text-xs font-bold outline-none uppercase" />
                            <input type="text" placeholder="Prijs" value={item.price || ''} onChange={e => {
                               const newItems = [...selectedSection.content!.items!]; newItems[idx] = { ...item, price: e.target.value };
                               updateSection(selectedSectionId, { content: { ...selectedSection.content, items: newItems } });
                            }} className="w-full p-2 border border-slate-100 rounded text-xs font-bold outline-none text-red-600" />
                            <textarea placeholder="Features (gescheiden door komma)" value={item.features || ''} onChange={e => {
                               const newItems = [...selectedSection.content!.items!]; newItems[idx] = { ...item, features: e.target.value };
                               updateSection(selectedSectionId, { content: { ...selectedSection.content, items: newItems } });
                            }} className="w-full p-2 border border-slate-100 rounded text-xs outline-none h-16 resize-none" />
                            <input type="text" placeholder="Button Tekst" value={item.cta || ''} onChange={e => {
                               const newItems = [...selectedSection.content!.items!]; newItems[idx] = { ...item, cta: e.target.value };
                               updateSection(selectedSectionId, { content: { ...selectedSection.content, items: newItems } });
                            }} className="w-full p-2 border border-slate-100 rounded text-xs outline-none" />
                            <label className="flex items-center gap-2 text-[10px] font-bold text-slate-500 mt-2">
                              <input type="checkbox" checked={item.highlighted || false} onChange={e => {
                                 const newItems = [...selectedSection.content!.items!]; newItems[idx] = { ...item, highlighted: e.target.checked };
                                 updateSection(selectedSectionId, { content: { ...selectedSection.content, items: newItems } });
                              }} className="w-3 h-3" />
                              Highlight dit pakket
                            </label>
                          </div>
                        )}

                      </div>
                    ))}
                    {(!selectedSection.content?.items || selectedSection.content.items.length === 0) && (
                      <p className="text-[10px] text-slate-400 italic">Nog geen items toegevoegd.</p>
                    )}
                  </div>
                )}

                {/* Image Manager */}
                {(selectedSection.type === 'hero' || selectedSection.type === 'about') && (
                  <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
                    <label className="text-[9px] font-black text-slate-400 uppercase block">Afbeelding</label>
                    <div className="flex gap-4">
                      <div className="w-20 h-20 bg-white border border-slate-200 rounded overflow-hidden shadow-inner flex items-center justify-center shrink-0">
                        {selectedSection.content?.imageUrl ? (
                          <img src={selectedSection.content.imageUrl} className="w-full h-full object-cover" />
                        ) : <ImageIcon className="text-slate-200" size={24} />}
                      </div>
                      <label className="flex-1 cursor-pointer group">
                        <div className="h-full border-2 border-dashed border-slate-200 rounded flex flex-col items-center justify-center hover:border-red-500 hover:bg-white transition-all text-slate-400 group-hover:text-red-600">
                          {isUploading === selectedSectionId ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
                          <span className="text-[8px] font-black uppercase mt-2">Replace File</span>
                        </div>
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => handleSectionImageUpload(e, selectedSectionId)} />
                      </label>
                    </div>
                  </div>
                )}

                {/* Styling Options */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[9px] font-black text-slate-400 uppercase mb-1.5 block">Achtergrond</label>
                    <input
                      type="color"
                      value={selectedSection.styles?.backgroundColor || '#ffffff'}
                      onChange={(e) => updateSection(selectedSectionId, { styles: { ...selectedSection.styles, backgroundColor: e.target.value } })}
                      className="w-full h-10 border-0 rounded cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className="text-[9px] font-black text-slate-400 uppercase mb-1.5 block">Tekst Kleur</label>
                    <input
                      type="color"
                      value={selectedSection.styles?.textColor || '#000000'}
                      onChange={(e) => updateSection(selectedSectionId, { styles: { ...selectedSection.styles, textColor: e.target.value } })}
                      className="w-full h-10 border-0 rounded cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className="text-[9px] font-black text-slate-400 uppercase mb-1.5 block">Knop Kleur</label>
                    <input
                      type="color"
                      value={selectedSection.styles?.buttonColor || '#e51b23'}
                      onChange={(e) => updateSection(selectedSectionId, { styles: { ...selectedSection.styles, buttonColor: e.target.value } })}
                      className="w-full h-10 border-0 rounded cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className="text-[9px] font-black text-slate-400 uppercase mb-1.5 block">Knop Tekst</label>
                    <input
                      type="color"
                      value={selectedSection.styles?.buttonTextColor || '#ffffff'}
                      onChange={(e) => updateSection(selectedSectionId, { styles: { ...selectedSection.styles, buttonTextColor: e.target.value } })}
                      className="w-full h-10 border-0 rounded cursor-pointer"
                    />
                  </div>
                </div>

                {/* Advanced Layout Options */}
                <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-lg border border-slate-200 mt-4">
                  <div>
                    <label className="text-[9px] font-black text-slate-400 uppercase mb-1.5 block">Uitlijning</label>
                    <select
                      value={selectedSection.styles?.align || 'left'}
                      onChange={(e) => updateSection(selectedSectionId, { styles: { ...selectedSection.styles, align: e.target.value as any } })}
                      className="w-full p-2 bg-white border border-slate-200 rounded text-xs font-bold outline-none"
                    >
                      <option value="left">Links</option>
                      <option value="center">Gecentreerd</option>
                      <option value="right">Rechts</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[9px] font-black text-slate-400 uppercase mb-1.5 block">Ruimte (Padding)</label>
                    <select
                      value={selectedSection.styles?.padding || 'normal'}
                      onChange={(e) => updateSection(selectedSectionId, { styles: { ...selectedSection.styles, padding: e.target.value as any } })}
                      className="w-full p-2 bg-white border border-slate-200 rounded text-xs font-bold outline-none"
                    >
                      <option value="small">Smal (Compact)</option>
                      <option value="normal">Normaal</option>
                      <option value="large">Ruim (Ademruimte)</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[9px] font-black text-slate-400 uppercase mb-1.5 block">Hoekstijl (Corners)</label>
                    <select
                      value={selectedSection.styles?.borderRadius || 'rounded'}
                      onChange={(e) => updateSection(selectedSectionId, { styles: { ...selectedSection.styles, borderRadius: e.target.value as any } })}
                      className="w-full p-2 bg-white border border-slate-200 rounded text-xs font-bold outline-none"
                    >
                      <option value="sharp">Scherp (Zakelijk)</option>
                      <option value="rounded">Afgerond (Modern)</option>
                      <option value="playful">Zeer Rond (Speels)</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[9px] font-black text-slate-400 uppercase mb-1.5 block">Schaduw</label>
                    <select
                      value={selectedSection.styles?.shadow || 'subtle'}
                      onChange={(e) => updateSection(selectedSectionId, { styles: { ...selectedSection.styles, shadow: e.target.value as any } })}
                      className="w-full p-2 bg-white border border-slate-200 rounded text-xs font-bold outline-none"
                    >
                      <option value="none">Geen Schaduw</option>
                      <option value="subtle">Subtiel (Zacht)</option>
                      <option value="prominent">Opvallend (Zwaar)</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Global Footer (Sticky) */}
        <div className="absolute bottom-0 left-0 w-full p-6 bg-white border-t border-slate-100 flex items-center justify-between">
          <button
            onClick={handleSave}
            className="flex-1 py-3 bg-slate-900 text-white rounded-md text-[10px] font-black uppercase tracking-widest hover:bg-red-600 transition-all shadow-xl flex items-center justify-center gap-2"
          >
            <Save size={14} /> Publish Website
          </button>
        </div>
      </div>

      {/* FULL PREVIEW CANVAS (Interactive Overlay) */}
      <div className="flex-1 bg-transparent pointer-events-none relative h-full">
        <LandingPage
          onLoginClick={() => { }}
          config={config}
          isEditing={true}
          vacancies={vacancies}
          selectedSectionId={selectedSectionId}
          onSectionClick={(id) => setSelectedSectionId(id)}
        />
      </div>
    </div>
  );
};

export default WebsiteEditor;
